import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '@/app/api/client';
import { toast } from 'react-hot-toast';
import { Camera, Loader2, Trash2 } from 'lucide-react';
import { apiConfig } from '@/config/api';

import { useAuth } from '@/context/AuthContext';

export const BusinessLogoSection = () => {
    const { refreshUserStatus } = useAuth();
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const response = await apiClient.get('/api/tenant/settings');
            if (response.data?.logoUrl) {
                const url = apiConfig.getUrl(response.data.logoUrl);
                // Use R2 key as version generator if available to allow proper caching (bust only on change)
                const version = response.data.logoR2Key ? `?v=${encodeURIComponent(response.data.logoR2Key)}` : '';
                setLogoUrl(url + version);
            }
        } catch (error) {
            console.error('Failed to fetch tenant settings:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogoClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file size (2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size exceeds 2MB limit');
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('logo', file);

        try {
            const response = await apiClient.post('/api/tenant/logo', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Use the returned R2Key for smart versioning, fallback to timestamp if not present
            const version = response.data.logoR2Key
                ? `?v=${encodeURIComponent(response.data.logoR2Key)}`
                : `?t=${Date.now()}`;

            const newLogoUrl = apiConfig.getUrl(response.data.logoUrl) + version;
            setLogoUrl(newLogoUrl);

            // Refresh global user status to reflect changes
            await refreshUserStatus();

            toast.success('Business logo updated successfully');
        } catch (error: any) {
            console.error('Error uploading logo:', error);
            toast.error(error.response?.data?.error || 'Failed to upload logo');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveLogo = async () => {
        if (!confirm('Are you sure you want to remove the business logo?')) return;

        setIsUploading(true);
        try {
            // Update settings to remove logoUrl
            await apiClient.put('/api/tenant/settings', {
                logoUrl: null,
                logoR2Key: null
            });
            setLogoUrl(null);
            await refreshUserStatus();
            toast.success('Business logo removed');
        } catch (error: any) {
            console.error('Error removing logo:', error);
            toast.error('Failed to remove logo');
        } finally {
            setIsUploading(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-6">
                <div className="relative group">
                    <div
                        className={`w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50 cursor-pointer hover:border-blue-500 transition-colors ${isUploading ? 'opacity-50' : ''}`}
                        onClick={handleLogoClick}
                    >
                        {logoUrl ? (
                            <img src={logoUrl} alt="Business Logo" className="w-full h-full object-contain" />
                        ) : (
                            <div className="text-center p-2">
                                <Camera className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                                <span className="text-xs text-gray-500">Upload Logo</span>
                            </div>
                        )}

                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 bg-white rounded-full p-1.5 shadow-sm">
                                <Camera className="w-4 h-4 text-gray-600" />
                            </div>
                        </div>
                    </div>

                    {isUploading && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                        </div>
                    )}
                </div>

                <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">Business Logo</h4>
                    <p className="text-sm text-gray-500 mt-1">
                        This logo will be automatically included in AI-generated images when the option is selected.
                        Recommended size: 512x512px or larger. PNG or JPG.
                    </p>

                    <div className="mt-3 flex gap-3">
                        <button
                            onClick={handleLogoClick}
                            disabled={isUploading}
                            className="text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                            {logoUrl ? 'Change Logo' : 'Upload Logo'}
                        </button>

                        {logoUrl && (
                            <button
                                onClick={handleRemoveLogo}
                                disabled={isUploading}
                                className="text-sm font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                            >
                                <Trash2 className="w-3 h-3" />
                                Remove
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
            />
        </div>
    );
};

export default BusinessLogoSection;
